// src/pages/Accounting/Reports/AccountList/components/ThreeDotMenu.tsx
// Self-contained 3-dot icon + dropdown. Opens Schedule / Add to Management reports.

import React, { useEffect, useRef, useState } from 'react';
import { MoreVertical, CalendarClock, BookmarkPlus } from 'lucide-react';

interface ThreeDotMenuProps {
  onScheduleReport: () => void;
  onAddToManagementReports: () => void;
}

export default function ThreeDotMenu({ onScheduleReport, onAddToManagementReports }: ThreeDotMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const act = (fn: () => void) => () => { fn(); setOpen(false); };

  return (
    <div ref={ref} className="relative group">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-8 h-8 rounded-md text-ink3 inline-flex items-center justify-center border-none bg-transparent cursor-pointer hover:bg-panel"
        aria-label="More options"
      >
        <MoreVertical size={15} strokeWidth={2} />
      </button>
      {!open && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 opacity-0 group-hover:opacity-100 transition-opacity bg-ink text-white text-[11px] font-semibold px-2 py-1 rounded-md whitespace-nowrap pointer-events-none z-40">
          More options
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-ink" />
        </span>
      )}
      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-[260px] bg-white border border-line rounded-xl shadow-[0_10px_32px_rgba(14,26,26,0.14)] py-1.5 z-50">
          <MenuItem icon={<CalendarClock size={15} strokeWidth={2} className="text-ink3" />} label="Schedule report" onClick={act(onScheduleReport)} />
          <MenuItem icon={<BookmarkPlus size={15} strokeWidth={2} className="text-ink3" />} label="Add to Management reports" onClick={act(onAddToManagementReports)} />
        </div>
      )}
    </div>
  );
}

function MenuItem({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[12.5px] text-ink text-left border-none bg-transparent cursor-pointer hover:bg-panel"
    >
      {icon}
      {label}
    </button>
  );
}